import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeState {
  theme: ThemeMode;
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeCtx = createContext<ThemeState | undefined>(undefined);

const THEME_STORAGE_KEY = 'myinvoice_theme_mode';

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Par défaut, le mode CLAIR est actif ("PAR DEFAUT VA ETRE LE MODE CLAIR")
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved === 'dark' || saved === 'light') return saved;
    } catch {
      /* ignore */
    }
    return 'light';
  });

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }

    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  function toggleTheme() {
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }

  function setTheme(mode: ThemeMode) {
    setThemeState(mode);
  }

  return (
    <ThemeCtx.Provider value={{ theme, isDark: theme === 'dark', toggleTheme, setTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
}

const defaultThemeState: ThemeState = {
  theme: 'light',
  isDark: false,
  toggleTheme: () => {},
  setTheme: () => {},
};

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeCtx);
  return ctx || defaultThemeState;
}

